<script lang="ts">
	import dayjs from 'dayjs';
	import { NewsProfileComponents } from './NewsProfileComponents';
	import { NewsSavedTags } from '#lib/features/news/components/NewsSavedTags/index.js';
	import { hasProfileTagId } from '#lib/websites/news/utils/hasProfileTagId/hasProfileTagId.js';
	import { page } from '$app/state';
	import { ShareIcon } from '$global/assets/icons/ShareIcon';
	import { Button } from '$global/components/Button';
	import { createDialogStores } from '$global/components/Dialog';
	import { ShareDialog } from '$global/components/Share/ShareDialog';
	import { Txt } from '$global/components/Txt';
	import type { NewsHomePageData } from '$routes/news/types';

	let data = $derived(page.data as NewsHomePageData);
	let profile = $derived(data.profile);

	const dataCyPrefix = 'news-profile';
	const dialogStores = createDialogStores();
	let showGlobalNav = $derived(page.data.showGlobalNav ?? true);

	const {
		states: { open },
	} = dialogStores;

	const handleOpenShareDialog = () => {
		if (showGlobalNav) {
			open.set(true);
		} else {
			(window.location as unknown as string) = `/news?shareApp=1&text=${encodeURIComponent(
				`${shareTitle} Profile`
			)}&url=${encodeURIComponent(page.url.href.replace('global_nav=0&', '').replace('&global_nav=0', ''))}`;
		}
	};

	// Share dialog data
	const shareUrl = $derived(page.url.href);
	const shareTitle = $derived(profile?.name || '');
	const shareIntro = $derived(profile?.summary || '');

	const getLifespanText = () => {
		if (profile?.year_birth && profile?.year_death) {
			return `${profile?.year_birth} - ${profile?.year_death}`;
		}

		if (profile?.year_birth) {
			return `born ${profile?.year_birth}`;
		}

		if (profile?.year_death) {
			return `dead ${profile?.year_death}`;
		}

		return '';
	};

	const getSubText = () => {
		const nationalityText = profile?.nationality;
		const lifespanText = getLifespanText();

		if (nationalityText && lifespanText) {
			return `${nationalityText}, ${lifespanText}`;
		}

		return nationalityText || lifespanText || '';
	};

	const subText = $derived(getSubText());

	const dateUpdated = $derived(profile?.date_published);

	type ProfileComponent = NonNullable<
		NonNullable<typeof profile>['profile_components']
	>[number];

	// Mirrors the condition NewsProfileComponents renders on, so the wrapper is
	// only shown when at least one component will actually appear inside it.
	const hasProfileComponents = $derived(
		(profile?.profile_components ?? []).some(
			(component: ProfileComponent) =>
				component?.HENI_News_Profile_Components_id
		)
	);

	// The save button reads its tag id from `related_tag`, so without one there
	// is nothing to save: the heart would render but its click handler bails
	// (after prompting a logged-out user to sign in for nothing). RESERVED
	// profiles like HENI have no related tag, so the button is hidden rather
	// than inert.
	const hasSavableTag = $derived(hasProfileTagId(profile));
</script>

<!-- The hero grows with its content rather than being pinned to a fixed height:
profiles without a nationality/lifespan subtext or a long summary (e.g. the
RESERVED HENI profile) would otherwise centre in 33.75rem and leave a large
empty band. Artist/geotag profiles with fuller data still reach that height. -->
<section
	class="relative flex items-center bg-gray-900 bg-cover px-4 py-16 sm:px-6"
>
	<div class="mx-auto w-full max-w-[650px] min-w-0">
		<div class="mb-1 flex items-center justify-between gap-3">
			<span class="flex min-w-0 items-center gap-3">
				<Txt
					variant="h1"
					class="text-gray-0 min-w-0 tracking-normal break-words"
				>
					{profile?.name}
				</Txt>

				{#if hasSavableTag}
					<NewsSavedTags name={profile?.name ?? ''} />
				{/if}
			</span>

			<Button
				class="shrink-0"
				dataCy={`${dataCyPrefix}-share`}
				variant="secondary"
				size="sm"
				onclick={handleOpenShareDialog}
			>
				Share
				{#snippet trailing()}
					<ShareIcon class="h-5 w-5" />
				{/snippet}
			</Button>
		</div>

		{#if subText}
			<Txt variant="h5" class="text-gray-0 mb-4 tracking-normal">
				{subText}
			</Txt>
		{/if}

		{#if dateUpdated}
			<Txt variant="body1" class="mb-4 text-[#8DABFF]">
				Updated: {dayjs(dateUpdated).format('DD MMM YYYY')}
			</Txt>
		{/if}

		<Txt class="text-gray-0 tracking-normal">
			{profile?.summary}
		</Txt>
	</div>
</section>

<!-- Skipped entirely when the profile has no components: the wrapper's padding and
bottom border would otherwise render as an empty white band under the hero. -->
{#if hasProfileComponents}
	<div class="flex justify-center border-b border-gray-200 bg-white px-4 py-12">
		<div class="max-w-[650px]">
			<NewsProfileComponents />
		</div>
	</div>
{/if}

<ShareDialog
	title={shareTitle}
	link={shareUrl}
	intro={shareIntro}
	dataCy={dataCyPrefix}
	{dialogStores}
	showTwitter={false}
/>
